// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Create Device: the unfinished drafts, and the button that starts a new one.
 *
 * The form itself is a dialog (components/ArchiveCreateDialog.tsx). This page is what is
 * left when it closes — every machine anyone has half-filed, since drafts are shared
 * (§10.2), each with who saved it last — so a draft is found without remembering its
 * device number, resumed in one click, and deleted when it is not wanted. The search
 * narrows the list by device ID or SN, type, device type or who saved it.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api/endpoints";
import { AddLabel } from "@/components/AddLabel";
import {
  ArchiveCreateDialog,
  type ArchiveKind,
  type DraftPayload,
  draftWorkflow,
  enteredCount,
  isUnnamedDraft,
} from "@/components/ArchiveCreateDialog";
import { moduleTypeLabel } from "@/components/BomModulesSummary";
import { Modal } from "@/components/Modal";
import { type Draft, deleteDraft, listDrafts } from "@/drafts/store";

/** The dialog is closed, open on a new robot or module (""), or open on a draft — by its
 *  device number, or by its own key: a module's SN, or one saved before it had a number. */
type Filing = { kind: ArchiveKind; deviceNo: string; draftSubject?: string } | null;

function kindOf(draft: Draft<DraftPayload>): ArchiveKind {
  return draft.workflow === "module-create" ? "MODULE" : "ROBOT";
}

export function ArchiveCreate() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [filing, setFiling] = useState<Filing>(null);
  const [query, setQuery] = useState("");
  /** The draft whose deletion is being confirmed, and what to call it. */
  const [deleting, setDeleting] = useState<{ draft: Draft<DraftPayload>; name: string } | null>(
    null,
  );

  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const userId = me.data?.user_id;
  const productTypes = useQuery({
    queryKey: ["product-types", "ACTIVE"],
    queryFn: () => api.productTypes.list({ status: "ACTIVE", limit: 200 }),
  });
  const moduleTypes = useQuery({ queryKey: ["module-types"], queryFn: api.moduleTypes.list });

  // The dialog refreshes this key as it saves, so the list is current when it closes.
  const drafts = useQuery({
    queryKey: ["archive-drafts", userId],
    queryFn: async () =>
      (await listDrafts(userId ?? "")).filter(
        (draft): draft is Draft<DraftPayload> =>
          draft.workflow === "archive-create" || draft.workflow === "module-create",
      ),
    enabled: Boolean(userId),
  });

  async function remove(draft: Draft<DraftPayload>) {
    if (!userId) return;
    await deleteDraft(userId, draftWorkflow(kindOf(draft)), draft.subject);
    await queryClient.invalidateQueries({ queryKey: ["archive-drafts", userId] });
  }

  const items = drafts.data ?? [];
  const productTypeName = (draft: Draft<DraftPayload>) =>
    productTypes.data?.items.find((item) => item.id === draft.payload.productTypeId)?.name;
  const typeLabel = (draft: Draft<DraftPayload>) =>
    kindOf(draft) === "MODULE"
      ? moduleTypeLabel(
          draft.payload.moduleType ?? "",
          moduleTypes.data ?? [],
          i18n.language === "en-US",
        )
      : t("archive.kindRobot");
  // One saved in this browser and not yet on the server is the signed-in user's own.
  const savedBy = (draft: Draft<DraftPayload>) => draft.updatedBy ?? me.data?.email ?? "";
  const identityOf = (draft: Draft<DraftPayload>) =>
    kindOf(draft) === "MODULE" ? (draft.payload.sn ?? "").trim() : draft.payload.deviceNo;
  const needle = query.trim().toLowerCase();
  const shown = needle
    ? items.filter((draft) =>
        [identityOf(draft), typeLabel(draft), productTypeName(draft), savedBy(draft)].some((text) =>
          text?.toLowerCase().includes(needle),
        ),
      )
    : items;
  // Both, side by side (§12): the final-assembly station files robots, a sub-assembly
  // station modules, and some benches do both.
  const startButtons = (large: boolean) => (
    <div className="button-group">
      <button
        type="button"
        className={large ? "button-primary button-large" : "button-primary"}
        onClick={() => setFiling({ kind: "ROBOT", deviceNo: "" })}
      >
        <AddLabel>{t("archive.addRobot")}</AddLabel>
      </button>
      <button
        type="button"
        className={large ? "button-large" : undefined}
        onClick={() => setFiling({ kind: "MODULE", deviceNo: "" })}
      >
        <AddLabel>{t("archive.addModule")}</AddLabel>
      </button>
    </div>
  );

  return (
    <section>
      <h1>{t("archive.create")}</h1>

      <div className="panel archive-drafts">
        <div className="page-head">
          <h2>{t("archive.drafts", { n: items.length })}</h2>
          {/* With no drafts the button sits in the empty state instead, as the one thing
              to do on the page. */}
          {items.length > 0 && startButtons(false)}
        </div>
        {items.length === 0 ? (
          <div className="empty-start">
            <p className="muted">{t("archive.draftsEmpty")}</p>
            {startButtons(true)}
          </div>
        ) : (
          <>
            <p className="hint">{t("archive.draftsHint")}</p>
            <div className="filters">
              <input
                type="search"
                aria-label={t("archive.draftSearch")}
                placeholder={t("archive.draftSearchPlaceholder")}
                autoComplete="off"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              {needle && (
                <span className="muted">
                  {t("archive.draftsShown", { shown: shown.length, total: items.length })}
                </span>
              )}
            </div>
            <table>
              <thead>
                <tr>
                  <th>{t("archive.kind")}</th>
                  <th>{t("archive.device")}</th>
                  <th>{t("archive.productType")}</th>
                  <th>{t("archive.draftEntered")}</th>
                  <th>{t("archive.draftSavedTime")}</th>
                  <th>{t("archive.draftSavedBy")}</th>
                  <th>
                    <span className="visually-hidden">{t("archive.draftActions")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((draft) => {
                  const kind = kindOf(draft);
                  // A module has no number until it is filed: its draft is known by its type,
                  // and by its SN when one was typed from the label.
                  const identity = identityOf(draft);
                  const device =
                    identity ||
                    t(kind === "MODULE" ? "moduleFiling.draftNoSn" : "archive.draftNoDevice");
                  return (
                    <tr key={draft.key}>
                      <td>
                        {kind === "MODULE" ? (
                          <span className="module-badge">{typeLabel(draft)}</span>
                        ) : (
                          typeLabel(draft)
                        )}
                      </td>
                      <td className={identity ? "mono" : "muted"}>{device}</td>
                      <td>{productTypeName(draft) ?? "—"}</td>
                      <td>{enteredCount(draft.payload)}</td>
                      <td>{new Date(draft.updatedAt).toLocaleString()}</td>
                      <td>{savedBy(draft) || "—"}</td>
                      <td className="actions">
                        <button
                          type="button"
                          aria-label={t("archive.draftResumeOf", { device })}
                          onClick={() =>
                            setFiling({
                              kind,
                              deviceNo: kind === "MODULE" ? "" : draft.payload.deviceNo,
                              draftSubject:
                                kind === "MODULE" || isUnnamedDraft(draft.subject)
                                  ? draft.subject
                                  : undefined,
                            })
                          }
                        >
                          {t("archive.draftResume")}
                        </button>
                        <button
                          type="button"
                          aria-label={t("archive.draftDeleteOf", { device })}
                          // Asked first: a draft is scanning nobody can get back (§10.2).
                          onClick={() => setDeleting({ draft, name: device })}
                        >
                          {t("common.delete")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {shown.length === 0 && <p className="muted">{t("archive.draftsNoMatch")}</p>}
          </>
        )}
      </div>

      {deleting && (
        <Modal
          title={t("archive.draftDeleteTitle")}
          onClose={() => setDeleting(null)}
          footer={
            <>
              <button
                type="button"
                className="button-danger"
                onClick={async () => {
                  await remove(deleting.draft);
                  setDeleting(null);
                }}
              >
                {t("common.delete")}
              </button>
              <button type="button" data-autofocus onClick={() => setDeleting(null)}>
                {t("common.cancel")}
              </button>
            </>
          }
        >
          <p>
            {t("archive.draftDeleteBody", {
              device: deleting.name,
              n: enteredCount(deleting.draft.payload),
            })}
          </p>
        </Modal>
      )}

      {filing && (
        <ArchiveCreateDialog
          kind={filing.kind}
          initialDeviceNo={filing.deviceNo}
          initialDraftSubject={filing.draftSubject}
          onClose={() => setFiling(null)}
        />
      )}
    </section>
  );
}
